import { useState, useEffect } from "react";
import Footer from "../Footer";
import Loading from "../Loading";
import AddGuestForm from "./AddGuestForm";
import { useNavigate } from "react-router-dom";
import SelectLocation from "../SelectLocation/SelectLocation";
import { defaultAppearance, DEFAUL_TIMEZONE } from "../../constants";
import Header from "../Header";
import FinishRegistration from "./FinishRegistration";
import VerificationSuccessful from "./VerificationSuccessful";
import { useParams, useLocation } from "react-router-dom";
import { routes } from "../../api/routes";
import { logError } from "../../services/log.service";
import Booking from "./Booking";
import Appointments from "../Appointments/Appointments";
import ErrorExists from "./ErrorExists";
import { Settings } from "luxon";
import LocationNoticeModal from "components/LocationNoticeModal";
import CheckInBlocked from "./CheckInBlocked";
import PolicyTermsModal from "components/PolicyTermsModal";
import { EcoTwoTone } from "@material-ui/icons";

function AddGuest(props) {
  const isBooking = props.isBooking ?? false;
  const navigate = useNavigate();
  const { id, catId } = useParams();
  const [loading, setLoading] = useState(true);
  const [showPolicyNotice, setShowPolicyNotice] = useState(false);
  const [reservationPolicy, setReservationPolicy] = useState(null);
  const [showNotice, setShowNotice] = useState(false);
  const [service, setService] = useState();
  const [business, setBusiness] = useState(null);
  const [blockCheckIn, setBlockCheckIn] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [businesses, setBusinesses] = useState(null);
  const [accountName, setAccountName] = useState(null);
  const [accountId, setAccountId] = useState(null);
  const [plan, setPlan] = useState(null);
  const [appearance, setAppearance] = useState(null);
  const [visit, setVisit] = useState(null);
  const [accountSettings, setAccountSettings] = useState(false);
  // The appointments landing page is meant to read as the business's own page, so the
  // "Powered by Waitly" footer is hidden while it's showing and restored once the guest
  // moves on to booking.
  const [onAppointmentsLanding, setOnAppointmentsLanding] = useState(false);

  // The route no longer distinguishes reservations from appointments (both live at
  // /book/:id, plan §8/§11) — checkInData decides server-side, on account.type, and
  // tags the result with `bookingFlow`. This is derived from that tag rather than a
  // route prop, so it's only meaningful once `business` has loaded.
  const isAppointmentFlow = isBooking && business?.bookingFlow === "appointments";

  useEffect(() => {
    let vid = localStorage.getItem(id + "previousConfirmation");
    getCheckInData(id, props.type, vid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, catId]);

  useEffect(() => {
    if (business?.settings?.timezone) {
      Settings.defaultZone = business.settings.timezone;
    }
  }, [business]);

  function useQuery() {
    return new URLSearchParams(useLocation().search);
  }
  const query = useQuery();

  function safeParse(x) {
    const parsed = parseInt(x, 10);
    if (isNaN(parsed)) {
      return undefined;
    }
    return parsed;
  }

  function postMessages(business, type) {
    postSetTitle(calculateTitle(business, type));
    postSetFooterText(calculateFooterText(business, type));
  }

  function postSetFooterText(text) {
    window.postMessage({ source: "waitly", command: "setFooterText", footerText: text });
  }
  function postSetTitle(title) {
    window.postMessage({ source: "waitly", command: "setTitle", title });
  }

  function selectLocationCallback(id) {
    const business = findBusinessById(id);
    setBusiness(business);
    postMessages(business, isBooking ? "book" : "checkin");
    if (isBooking) {
      navigate(`/book/${id}`);
    } else {
      navigate(`/checkin/${id}`);
    }
  }

  const findBusinessById = id => {
    for (let b of businesses) {
      if (b.id === id) {
        return b;
      }
    }
  };

  function getGetCategory(catId, categories) {
    const categoryId = safeParse(catId);
    if (categoryId && categories && categories.length > categoryId - 1) {
      return categories[categoryId - 1].name;
    } else {
      return undefined;
    }
  }

  function sendLoadingCompleteMessage() {
    if (window.parent) {
      window.parent.postMessage({ source: "waitly", command: "loadingComplete" }, "*");
    }
  }

  function calculateFooterText(business, type) {
    if (type === "checkin") {
      return `${business.name} partners with Waitly to manage their Waitlist. `;
    } else if (type === "book") {
      return `${business.name} partners with Waitly to manage their Bookings. `;
    } else if (type === "appointment") {
      return `${business.name} partners with Waitly to manage their Appointments. `;
    }
  }

  function calculateTitle(business, type) {
    if (type === "checkin") {
      return `Waitlist check-in at ${business.name} `;
    } else if (type === "book") {
      return `Bookings at ${business.name}`;
    } else if (type === "appointment") {
      return `Appointments at ${business.name}`;
    }
  }

  function handleOk() {
    setShowNotice(false);
    requestLocation(business);
  }
  function handlePolicyOk() {
    setShowPolicyNotice(false);
  }
  function checkLocationPermissions(business) {
    if ("geolocation" in navigator) {
      navigator.permissions.query({ name: "geolocation" }).then(function (result) {
        if (result.state === "granted") {
          requestLocation(business);
        } else if (result.state === "prompt") {
          setPermissionDenied(false);
          setShowNotice(true);
        } else if (result.state === "denied") {
          setPermissionDenied(true);
          setBlockCheckIn(true);
        }
      });
    }
  }

  function requestLocation(business) {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        position => {
          setBlockCheckIn(
            !areCoordinatesClose(
              formatGeoLocation(business.settings.address.geo),
              position.coords,
              business.settings.geoFenceRadius
            )
          );
        },
        err => {
          console.log("error", err);
          setPermissionDenied(true);
          setBlockCheckIn(true);
        }
      );
    } else {
      alert("Geolocation is not available in your browser");
    }
  }

  function formatGeoLocation(coords) {
    return { latitude: coords.lat, longitude: coords.lng };
  }

  function getDistance(coord1, coord2) {
    const R = 6371000; // Radius of Earth in meters
    const lat1 = (coord1.latitude * Math.PI) / 180;
    const lat2 = (coord2.latitude * Math.PI) / 180;
    const deltaLat = ((coord2.latitude - coord1.latitude) * Math.PI) / 180;
    const deltaLon = ((coord2.longitude - coord1.longitude) * Math.PI) / 180;

    const a =
      Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
      Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLon / 2) * Math.sin(deltaLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Distance in meters
  }

  // Function to check if two coordinates are within 1000 meters of each other
  function areCoordinatesClose(coord1, coord2, maxDistance = 152) {
    const distance = getDistance(coord1, coord2);
    return distance <= maxDistance;
  }

  function getCheckInData(businessId, type, vid) {
    // checkInData decides reservations vs. appointments itself, on account.type, and
    // tags the result with `bookingFlow` — /book/:id no longer needs a separate
    // endpoint or route to pick between them (plan §8/§11).
    let url = `${routes.checkInData}?id=${businessId}&type=${type}&isBooking=${isBooking}`;
    if (!isBooking && vid) {
      url += `&vid=${vid}`;
    }

    fetch(url)
      .then(response => response.json())
      .then(json => {
        setAppearance({ ...defaultAppearance, ...(json.appearance || {}) });
        if (!json.businesses?.length) {
          setLoading(false);
          sendLoadingCompleteMessage();
          return;
        }
        if (props.type === "checkin" || props.type === "book") {
          setService(getGetCategory(catId, json.businesses[0].settings.services));

          setBusiness(json.businesses[0]);

          const flowType = json.businesses[0]?.bookingFlow === "appointments" ? "appointment" : type;
          postMessages(json.businesses[0], flowType);
        }
        setLoading(false);
        sendLoadingCompleteMessage();
        setAccountId(json.accountId);
        setBusinesses(json.businesses);
        const isReservationsAccount = ["restaurant", "bar"].includes(json.accountType?.toLowerCase());
        const policy = isReservationsAccount ? json.businesses[0]?.settings?.reservationPolicy : null;
        setReservationPolicy(policy || null);
        setShowPolicyNotice(!!policy);
        setVisit(json.visit);
        setAccountName(json.accountName);
        setAccountSettings(json.accountSettings);
        setPlan(json.plan);
        if (shouldRedirectToCheckIn(json.businesses)) {
          redirectToCheckIn(json.businesses[0]);
        }
        if (shouldRedirectToBooking(json.businesses)) {
          redirectToBooking(json.businesses[0]);
        }
        if (props.type === "checkin" && json.businesses[0].settings?.geoFence) {
          checkLocationPermissions(json.businesses[0]);
          document.addEventListener("visibilitychange", function () {
            if (document.visibilityState === "visible") {
              checkLocationPermissions(json.businesses[0]);
            }
          });
        }
      })
      .catch(err => {
        console.log("error: ", err);
        logError("AddGuest fetch appearance", err, { businessId });
        // set default appearance
        setAppearance(defaultAppearance);
        setLoading(false);
      });
  }

  function getMaxPartySize() {
    return business?.settings?.waitlist?.maxPartySize || 8;
  }

  function getTimezone() {
    return business?.settings?.timezone || DEFAUL_TIMEZONE;
  }

  const getOpenCount = businesses => {
    let count = 0;
    if (!businesses) {
      return count;
    }
    for (let b of businesses) {
      if (!dayMarkedAsClosed(b)) {
        count++;
      }
    }
    return count;
  };

  const getSelfBookingCount = businesses => {
    let count = 0;
    if (!businesses) {
      return count;
    }
    for (let b of businesses) {
      if (b.settings?.selfBooking) {
        count++;
      }
    }
    return count;
  };

  const isSomeOpenForBooking = businesses => {
    if (!businesses || !businesses.length) {
      return false;
    }

    return (
      businesses
        // map businesses to boolean values that mean if some day open
        .map(b => b.settings?.reservations?.hours?.some(h => !h.closed) || b.shifts?.length > 0)
        // is some day open ([true, false].some(el => el === true))
        .some(el => el)
    );
  };

  const dayMarkedAsClosed = business => {
    return business.settings.disabledForm || business.settings.waitlistClosed;
  };

  const shouldDisplayLinkNotAvailable = () => {
    return plan === "free" && !isBooking;
  };

  const shouldShowNotAcceptingCheckins = () => {
    // Appointments are a flavor of isBooking now (plan §8/§11), and were never a
    // check-in flow: the payload carries no `selfCheckIn`, so without this they would
    // always fail the check below and never render.
    if (isBooking) return false;
    return (
      (business && (business.waitlistClosed || !business?.settings?.selfCheckIn)) || getOpenCount(businesses) === 0
    );
  };

  const shouldShowNotAcceptingBooking = () => {
    if (!isBooking || isAppointmentFlow) return false;

    if (plan !== "pro" && plan !== "enterprise") {
      return true;
    }
    return (business && !business?.settings?.selfBooking) || !isSomeOpenForBooking(businesses);
  };

  const shouldShowNotAcceptingAppointments = () => {
    if (!isAppointmentFlow) return false;
    // checkInData always returns a result for a non-restaurant/bar account (it no
    // longer 404s on the §8 gate — see checkInData.ts), so an empty business here
    // means the business doc itself doesn't exist, not "not accepting".
    return !business || !business?.settings?.selfBooking;
  };

  const shouldRedirectToCheckIn = businesses => {
    return getOpenCount(businesses) === 1 && props.type === "select-location" && !isBooking;
  };

  const shouldRedirectToBooking = businesses => {
    return getSelfBookingCount(businesses) === 1 && props.type === "select-location" && isBooking;
  };

  const redirectToCheckIn = business => {
    setBusiness(business);
    navigate(`/checkin/${business.id}`);
  };

  const redirectToBooking = business => {
    setBusiness(business);
    navigate(`/book/${business.id}`);
  };

  const renderContent = () => {
    if (shouldDisplayLinkNotAvailable()) {
      return <div className="checkin-error-message">Sorry. This link is not available.</div>;
    } else if (shouldShowNotAcceptingCheckins()) {
      return (
        <div>
          <Header business={business} appearance={appearance} fontColor={appearance.addGuestBusinessNameColor} />
          <div className="checkin-error-message">
            We are currently not accepting self check-ins.
            <br />
            <br />
            Please see our staff to be served.
          </div>
        </div>
      );
    } else if (shouldShowNotAcceptingAppointments()) {
      return (
        <div>
          <Header business={business} appearance={appearance} fontColor={appearance.addGuestBusinessNameColor} />
          <div className="checkin-error-message">
            We are currently not accepting online appointments.
            <br />
            <br />
            Please contact us to book.
          </div>
        </div>
      );
    } else if (shouldShowNotAcceptingBooking()) {
      return (
        <div>
          <Header business={business} appearance={appearance} fontColor={appearance.addGuestBusinessNameColor} />
          <div className="checkin-error-message">
            We are currently not accepting online reservations.
            <br />
            <br />
            Please see our staff to be served.
          </div>
        </div>
      );
    } else {
      return (
        <>
          <PolicyTermsModal show={showPolicyNotice} handleOk={handlePolicyOk} policy={reservationPolicy} />
          <LocationNoticeModal business={business} show={showNotice} handleOk={handleOk} />
          <Header
            business={business}
            accountName={accountName}
            appearance={appearance}
            fontColor={appearance.addGuestBusinessNameColor}
          />
          <div>
            {props.type === "checkin" ? (
              <>
                {blockCheckIn ? (
                  <CheckInBlocked business={business} appearance={appearance} denied={permissionDenied} />
                ) : (
                  <AddGuestForm
                    visit={visit}
                    accountName={accountName}
                    businessId={id}
                    business={business}
                    maxPartySize={getMaxPartySize()}
                    appearance={appearance}
                    accountSettings={accountSettings}
                    service={service}
                  />
                )}
              </>
            ) : props.type === "checkin-confirm" ? (
              <FinishRegistration
                pendingVisitId={query.get("pendingVisitId")}
                code={query.get("code")}
                businessId={id}
                appearance={appearance}
              />
            ) : props.type === "checkin-exists-error" ? (
              <ErrorExists hash={query.get("hash")} appearance={appearance} />
            ) : props.type === "select-location" ? (
              <SelectLocation
                appearance={appearance}
                openCount={getOpenCount(businesses)}
                selectLocationCallback={selectLocationCallback}
                businesses={businesses}
              />
            ) : props.type === "book" && isAppointmentFlow ? (
              <Appointments
                accountName={accountName}
                businessId={id}
                accountId={accountId}
                business={business}
                appearance={appearance}
                onLandingStepChange={setOnAppointmentsLanding}
              />
            ) : props.type === "book" ? (
              <Booking
                accountName={accountName}
                businessId={id}
                accountId={accountId}
                business={business}
                maxPartySize={getMaxPartySize()}
                appearance={appearance}
                service={service}
              />
            ) : (
              <VerificationSuccessful hash={query.get("hash")} appearance={appearance} />
            )}
          </div>
        </>
      );
    }
  };

  return loading ? (
    <div>
      <Loading />
    </div>
  ) : (
    <div className="add-guest-page" style={{ background: appearance.addGuestBackground }}>
      {renderContent()}
      <Footer appearance={appearance} fixed={false} hide={onAppointmentsLanding} />
    </div>
  );
}

export default AddGuest;
